import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Table, Button, Input, Form, Modal, message, Space, Popconfirm, Switch, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { getAllBanks, createBank, updateBank, deleteBank, getAccountTree } from '../../api/accounting';
import useGlobalFilters from '../../../../hooks/useGlobalFilters';

// Helper function to render hierarchical account options
const renderAccountOptions = (accounts) => {
  if (!accounts || !accounts.length) return null;

  const buildIndentedOptions = (items, depth = 0, includeSelf = true) => {
    return items.flatMap((account) => {
      const hasChildren =
        Array.isArray(account.children) && account.children.length > 0;
      const indent = depth > 0 ? `${"\u2014 ".repeat(depth)}` : "";

      const nodes = [];
      const selectable = !!(account.is_postable || !hasChildren);
      const label = account.code
        ? `${account.code} - ${account.name}`
        : account.name;

      if (includeSelf) {
        nodes.push(
          <Select.Option
            key={account.id}
            value={account.id}
            disabled={!selectable}
          >
            {`${indent}${label}`}
          </Select.Option>
        );
      }

      if (hasChildren) {
        nodes.push(...buildIndentedOptions(account.children, depth + 1, true));
      }
      return nodes;
    });
  };

  try {
    return accounts.map((root) => (
      <Select.OptGroup
        key={root.id}
        label={`${root.code || ""} - ${root.name}`}
      >
        {buildIndentedOptions([root], 0, false)}
      </Select.OptGroup>
    ));
  } catch (error) {
    console.error("Error rendering account options:", error);
    return null;
  }
};

const BanksTab = ({ selectedBrand = null }) => {
  const { selectedBrands } = useGlobalFilters();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render counter
  
  // Track current fetch to prevent race conditions
  const fetchIdRef = React.useRef(0);
  
  // Use global filters if available, otherwise fall back to selectedBrand prop
  const brandsToUse = useMemo(() => {
    return selectedBrands && selectedBrands.length > 0 ? selectedBrands : (selectedBrand ? [selectedBrand] : []);
  }, [selectedBrands, selectedBrand]);
  
  // Create stable brand IDs string for dependency tracking
  const brandIdsString = useMemo(() => {
    return brandsToUse.map(b => b.id).join(',');
  }, [brandsToUse]);

  // Fetch accounts for chart of accounts dropdown
  const fetchAccounts = useCallback(async () => {
    // Use first brand for account tree (accounts are brand-specific)
    const brandForAccounts = brandsToUse.length > 0 ? brandsToUse[0] : null;
    if (!brandForAccounts) {
      setAccounts([]);
      return;
    }
    setAccountsLoading(true);
    try {
      const filters = { brand: brandForAccounts.id };
      const response = await getAccountTree(filters);
      setAccounts(response || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      message.error('Failed to fetch chart of accounts');
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [brandsToUse]);

  // Fetch banks on component mount or when brands change
  useEffect(() => {
    fetchBanks();
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandIdsString]);

  const fetchBanks = async (filters = {}, useCurrentFilters = false) => {
    // Increment fetch ID to track this request
    const currentFetchId = ++fetchIdRef.current;
    
    setLoading(true);
    try {
      // Add brand filter(s) - support multiple brands
      if (brandsToUse.length > 0) {
        // Pass multiple brand IDs as comma-separated string
        const brandIds = brandsToUse.map(b => b.id).join(',');
        filters.brand = brandIds;
      }
      
      // If useCurrentFilters is true, apply current search/filter state
      if (useCurrentFilters) {
        if (searchText) filters.name = searchText;
        if (activeFilter !== null) filters.active = activeFilter;
      }
      
      const response = await getAllBanks(filters);
      
      // Only update state if this is still the latest request
      if (currentFetchId !== fetchIdRef.current) {
        return; // A newer request has been made, ignore this result
      }
      
      setBanks([...(response.data || [])]); // Force new array reference
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) return;
      console.error('Error fetching banks:', error);
      message.error('Failed to fetch banks');
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    const filters = {};
    if (searchText) filters.name = searchText;
    if (activeFilter !== null) filters.active = activeFilter;
    fetchBanks(filters);
  };

  const resetSearch = () => {
    setSearchText('');
    setActiveFilter(null);
    fetchBanks();
  };

  const showCreateModal = () => {
    setEditingBank(null);
    form.resetFields();
    form.setFieldsValue({
      active: true
    });
    setIsModalVisible(true);
    // Refresh accounts list when opening modal to include newly created accounts
    fetchAccounts();
  };

  const showEditModal = (bank) => {
    setEditingBank(bank);
    // Refresh accounts list when opening modal to include newly created accounts
    fetchAccounts();
    
    // Extract chart_of_account ID - handle both object and ID formats
    let chartOfAccountId = null;
    if (bank.chart_of_account) {
      if (typeof bank.chart_of_account === 'object' && bank.chart_of_account.id) {
        chartOfAccountId = bank.chart_of_account.id;
      } else if (typeof bank.chart_of_account === 'number' || typeof bank.chart_of_account === 'string') {
        chartOfAccountId = typeof bank.chart_of_account === 'string' 
          ? parseInt(bank.chart_of_account, 10) 
          : Number(bank.chart_of_account);
      }
    }
    
    form.setFieldsValue({
      name: bank.name,
      account_number: bank.account_number,
      account_name: bank.account_name,
      routing_number: bank.routing_number || '',
      swift_code: bank.swift_code || '',
      iban: bank.iban || '',
      address: bank.address || '',
      notes: bank.notes || '',
      chart_of_account: chartOfAccountId,
      active: bank.active
    });
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Add brand - use first brand from global filters or selectedBrand prop
      const brandForCreation = brandsToUse.length > 0 ? brandsToUse[0] : selectedBrand;
      if (brandForCreation) {
        values.brand = brandForCreation.id;
      } else {
        message.error('Please select a brand first');
        return;
      }
      
      // Ensure active field is included
      if (values.active === undefined) {
        values.active = true;
      }
      
      // Make sure optional fields are included as empty strings if undefined
      const optionalFields = ['routing_number', 'swift_code', 'iban', 'address', 'notes'];
      optionalFields.forEach(field => {
        if (values[field] === undefined) {
          values[field] = '';
        }
      });
      
      // Handle chart_of_account - it can be null/undefined (nullable ForeignKey)
      // Convert to number if it's a string or ensure it's properly formatted
      if (values.chart_of_account !== undefined && values.chart_of_account !== null && values.chart_of_account !== '') {
        // Ensure chart_of_account is a number (ID)
        values.chart_of_account = typeof values.chart_of_account === 'string' 
          ? parseInt(values.chart_of_account, 10) 
          : Number(values.chart_of_account);
      } else {
        // If undefined or empty string, set to null explicitly
        values.chart_of_account = null;
      }
      
      console.log('Submitting bank with values:', values);
      console.log('Chart of Account ID:', values.chart_of_account);
      
      if (editingBank) {
        const response = await updateBank(editingBank.id, values);
        // Handle both axios wrapped response and direct data response
        const updatedBank = response?.data?.id ? response.data : (response?.id ? response : { ...editingBank, ...values });
        message.success('Bank updated successfully');
        
        // Immediately update the item in state (optimistic update)
        setBanks(prevBanks => {
          const newBanks = prevBanks.map(b => 
            b.id === editingBank.id ? { ...b, ...values, ...updatedBank } : b
          );
          return [...newBanks]; // Force new array reference
        });
      } else {
        // CREATE NEW BANK
        await createBank(values);
        
        message.success('Bank created successfully');
        
        // Clear search to show all banks
        setSearchText('');
        setActiveFilter(null);
        
        // Refetch banks to get the newly created one with server-generated fields
        await fetchBanks();
        
        // Force table re-render
        setRefreshKey(prev => prev + 1);
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('banksUpdated'));
        
        setIsModalVisible(false);
        form.resetFields();
        return; // Exit early for create path
      }
      
      // For UPDATE path
      setIsModalVisible(false);
      form.resetFields();
      setRefreshKey(prev => prev + 1);
      setSearchText('');
      setActiveFilter(null);
      window.dispatchEvent(new CustomEvent('banksUpdated'));
    } catch (error) {
      console.error('Error saving bank:', error.response?.data || error);
      message.error('Failed to save bank: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (bankId) => {
    try {
      await deleteBank(bankId);
      message.success('Bank deleted successfully');
      
      // Immediately remove the deleted item from state (optimistic update)
      setBanks(prevBanks => prevBanks.filter(b => b.id !== bankId));
      
      // Force table re-render
      setRefreshKey(prev => prev + 1);
      
      // Dispatch event to notify other components to refresh banks
      window.dispatchEvent(new CustomEvent('banksUpdated'));
    } catch (error) {
      console.error('Error deleting bank:', error);
      message.error('Failed to delete bank');
    }
  };

  const columns = [
    {
      title: 'Bank Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Account Name',
      dataIndex: 'account_name',
      key: 'account_name',
    },
    {
      title: 'Account Number',
      dataIndex: 'account_number',
      key: 'account_number',
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (active ? 'Active' : 'Inactive'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => showEditModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this bank?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="danger" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card title="Banks Management">
      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={handleSearch}
          style={{ width: 200 }}
          prefix={<SearchOutlined />}
        />
        <Button type="primary" onClick={handleSearch}>Search</Button>
        <Button onClick={resetSearch}>Reset</Button>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={showCreateModal}
        >
          Add Bank
        </Button>
      </Space>

      <Table
        key={refreshKey}
        columns={columns}
        dataSource={banks}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingBank ? 'Edit Bank' : 'Add Bank'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={600}
        okText={editingBank ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Bank Name"
            rules={[{ required: true, message: 'Please enter bank name' }]}
          >
            <Input placeholder="e.g., Chase, Bank of America" />
          </Form.Item>
          
          <Form.Item
            name="account_name"
            label="Account Name"
            rules={[{ required: true, message: 'Please enter account name' }]}
          >
            <Input placeholder="e.g., Business Checking" />
          </Form.Item>
          
          <Form.Item
            name="account_number"
            label="Account Number"
            rules={[{ required: true, message: 'Please enter account number' }]}
          >
            <Input placeholder="e.g., 123456789" />
          </Form.Item>
          
          <Form.Item
            name="routing_number"
            label="Routing Number"
          >
            <Input placeholder="e.g., 021000021" />
          </Form.Item>
          
          <Form.Item
            name="swift_code"
            label="SWIFT Code"
          >
            <Input placeholder="e.g., CHASUS33" />
          </Form.Item>
          
          <Form.Item
            name="iban"
            label="IBAN"
          >
            <Input placeholder="e.g., US29NWBK60161331926819" />
          </Form.Item>
          
          <Form.Item
            name="address"
            label="Bank Address"
          >
            <Input.TextArea rows={2} placeholder="Physical address of the bank" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="Notes"
          >
            <Input.TextArea rows={3} placeholder="Additional notes about this bank account" />
          </Form.Item>
          
          <Form.Item
            name="chart_of_account"
            label="Chart of Account"
            help="Select the chart of account associated with this bank. Only postable accounts (leaf nodes) can be selected."
          >
            <Select
              placeholder="Select a chart of account"
              loading={accountsLoading}
              showSearch
              filterOption={(input, option) => {
                const label = option?.label || option?.children || '';
                return String(label).toLowerCase().includes(input.toLowerCase());
              }}
              allowClear
              optionFilterProp="children"
              notFoundContent={accountsLoading ? "Loading accounts..." : "No accounts found"}
            >
              {renderAccountOptions(accounts)}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="active"
            label="Active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default BanksTab;
